import React, { useState, useEffect } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { getIndividualTerminalAssessment } from "../../actions/IndividualTerminalAssessment.action";
import { getGradeList } from "../../actions/Grade.action";
import { years } from "../../constants/MonthsAndYears";
import { termArray } from "../../constants/termArray";
import { toast } from "react-toastify";

const IndividualTerminalAssessmentForm = ({
  grades,
  getGradeList,
  getIndividualTerminalAssessment,
  publicView = false,
}) => {
  const [formData, setFormData] = useState({
    gradeId: "",
    shiftId: "",
    batchId: "",
    year: new Date().getFullYear(),
    term: "1",
    uid: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getGradeList();
  }, [getGradeList]);

  // No need for separate shift and batch calls as they are nested in grades

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset dependent fields when parent field changes
    if (name === "gradeId") {
      setFormData((prev) => ({
        ...prev,
        shiftId: "",
        batchId: "",
        uid: "",
      }));
    } else if (name === "shiftId") {
      setFormData((prev) => ({
        ...prev,
        batchId: "",
        uid: "",
      }));
    } else if (name === "batchId") {
      setFormData((prev) => ({
        ...prev,
        uid: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.gradeId ||
      !formData.shiftId ||
      !formData.batchId ||
      !formData.uid
    ) {
      toast.error("Please fill in all required fields!");
      return;
    }

    setIsLoading(true);
    try {
      const data = await getIndividualTerminalAssessment({
        gradeId: parseInt(formData.gradeId),
        shiftId: parseInt(formData.shiftId),
        batchId: parseInt(formData.batchId),
        year: parseInt(formData.year),
        term: parseInt(formData.term),
        uid: parseInt(formData.uid),
      });

      if (data && data.success) {
        toast.success("Assessment data loaded successfully!");

        // Scroll down 100px if this is a public component
        if (publicView) {
          setTimeout(() => {
            window.scrollBy({
              top: 500,
              behavior: "smooth",
            });
          }, 100); // Small delay to ensure toast is visible
        }
      } else {
        toast.error(data?.message || "No data found for the selected student");
      }
    } catch (error) {
      toast.error("Failed to load assessment data");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Card bg="white" text="dark" className={`crd shadow`}>
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="gradeId" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                name="gradeId"
                value={formData.gradeId}
                onChange={handleInputChange}
              >
                <option value="">Select Class</option>
                {grades?.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {formData.gradeId !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shiftId" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  name="shiftId"
                  value={formData.shiftId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Shift</option>
                  {grades
                    ?.filter((g) => g.id === parseInt(formData.gradeId))[0]
                    ?.shifts?.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {formData.gradeId !== "" && formData.shiftId !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batchId" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select
                  name="batchId"
                  value={formData.batchId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Batch</option>
                  {grades
                    ?.filter((g) => g.id === parseInt(formData.gradeId))[0]
                    ?.batches?.filter(
                      (bt) => bt.shiftId === parseInt(formData.shiftId)
                    )
                    ?.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}

            {formData.gradeId !== "" &&
            formData.shiftId !== "" &&
            formData.batchId !== "" ? (
              <Col
                md={3}
                className="d-flex justify-content-end align-items-end py-3 d-none d-md-flex"
              >
                <Button
                  onClick={handleSubmit}
                  variant="primary"
                  type="submit"
                  disabled={isLoading || !formData.uid}
                  className="btn_primary"
                >
                  {isLoading ? "Loading..." : "Get Assessment"}
                </Button>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col md={3} className="py-3">
              <InputGroup className="mb-3 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center">
                  <label htmlFor="uid" className="d-block">
                    Student Roll
                  </label>
                </div>
                <Form.Control
                  placeholder="Type roll of the student..."
                  name="uid"
                  type="number"
                  className="w-100"
                  value={formData.uid}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, uid: e.target.value }));
                  }}
                />
              </InputGroup>
            </Col>

            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                name="year"
                value={formData.year}
                onChange={handleInputChange}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="term" className="d-block">
                  Term
                </label>
              </div>
              <Form.Select
                name="term"
                value={formData.term}
                onChange={handleInputChange}
              >
                {termArray.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {/* Mobile Submit Button */}
          {formData.gradeId !== "" &&
            formData.shiftId !== "" &&
            formData.batchId !== "" && (
              <Row className="d-md-none">
                <Col className="py-3">
                  <Button
                    onClick={handleSubmit}
                    variant="primary"
                    type="submit"
                    disabled={isLoading || !formData.uid}
                    className="btn_primary w-100"
                  >
                    {isLoading ? "Loading..." : "Get Assessment"}
                  </Button>
                </Col>
              </Row>
            )}
        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

const mapDispatchToProps = {
  getGradeList,
  getIndividualTerminalAssessment,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(IndividualTerminalAssessmentForm);
